import { User } from '@prisma/client';
import { SafeUser } from '@zond/api-interfaces';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@zond/ui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import UserForm from './user-form';
import { useDeleteUser, useUsers } from './user.queries';
import { UsersTable } from './users.table';

export const Users = () => {
  const { t } = useTranslation('users');
  const [open, setOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<SafeUser | undefined>(undefined);
  const [mode, setMode] = React.useState<'create' | 'edit'>('create');
  const [initialData, setInitialData] = React.useState<SafeUser | undefined>(undefined);
  const { mutate: deleteUser } = useDeleteUser();
  const { refetch } = useUsers();

  const handleEdit = (user: SafeUser) => {
    setOpen(true);
    setMode('edit');
    setInitialData(user);
  };

  const handleDelete = (user: SafeUser) => {
    setDeleteDialogOpen(true);
    setSelectedUser(user);
  };

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    if (!open) {
      setInitialData(undefined);
    }
  };

  const handleDeleteDialogOpenChange = (open: boolean) => {
    setDeleteDialogOpen(open);
    if (!open) {
      setSelectedUser(undefined);
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedUser) {
      deleteUser(selectedUser.id.toString(), {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setSelectedUser(undefined);
          refetch();
        },
      });
    }
  };

  return (
    <main>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-2xl">{t('title')}</CardTitle>
              <CardDescription>{t('description')}</CardDescription>
            </div>
            <div>
              <Button
                size="sm"
                onClick={() => {
                  setOpen(true);
                  setMode('create');
                  setInitialData(undefined);
                }}
              >
                {t('actions.addUser')}
              </Button>
              <UserForm open={open} setOpen={handleOpenChange} mode={mode} initialData={initialData} />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <UsersTable onEdit={handleEdit} onDelete={handleDelete} />
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={handleDeleteDialogOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('deleteDialog.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              {t('common:actions.continue')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
};
